/** /zh/comparisons/obsidian — Chinese version. See Obsidian.tsx for design notes. */
import { MarketingShell } from '../MarketingShell';

export function ObsidianZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/comparisons/obsidian"
      documentTitle="给 AI 产品工作的 Obsidian 替代方案：Draftlize vs Obsidian · Draftlize"
      documentDescription="在找做产品决策的 Obsidian 替代方案？Obsidian 是给人事后读的顶级本地笔记库；Draftlize 是给 AI agent 读写的决策 substrate——改一个决策，所有依赖它的东西自动标 stale。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">对比 · vs Obsidian</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Obsidian 是顶级的笔记库，<br/>
            <em>给一个人事后慢慢读。</em>
          </h1>
          <p className="lede">
            Obsidian 是个漂亮的本地库：纯 markdown、双向链接、一张等未来的你回来读时会回报你的图谱。但产品决策现在有了不一样的首要读者——Claude Code、Cursor 里的 AI agent，也有了不一样的诉求：改一个决策，所有依赖它的东西都该知道。Markdown 笔记做不到——它是给人读的静态文本，没有依赖追踪，agent 也无法结构化地写回。Draftlize 不是一个更好的笔记软件，它是为另一件事换了另一种形态。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">TL;DR</span>
            <h2 className="serif">Obsidian 把笔记存给 <em>你</em>，Draftlize 把决策存给 <em>agent</em>。</h2>
          </div>
          <p>Obsidian 是个出色的个人知识库，这点没争议。但知识库是围绕「人事后读文本」搭的——当读者是 AI agent、内容是一张互相依赖的活的产品决策网时，这个形态恰好不对。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Draftlize</th>
              <th>Obsidian</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>你持久化的单元</td><td className="ours yes">结构化的决策 / spec / PRD 卡片</td><td className="partial">自由格式的 markdown 笔记</td></tr>
            <tr><td>改一处，自动标记受影响的地方</td><td className="ours yes">依赖图 + stale 标记</td><td className="no">双链只指向，但没有「变 stale」</td></tr>
            <tr><td>AI agent 能结构化地写回</td><td className="ours yes">agent 经 MCP 创建、更新卡片</td><td className="no">agent 能改文本，但只是散文</td></tr>
            <tr><td>自相矛盾，自动浮现</td><td className="ours yes">跨讨论冲突检测</td><td className="no">不会主动比对你的笔记</td></tr>
            <tr><td>每个决策有固定位置可引用</td><td className="ours yes">卡片可寻址（URL / ID）</td><td className="partial">有笔记路径，但没有决策身份</td></tr>
            <tr><td>以 AI agent 为首要读者来设计</td><td className="ours yes">卡片图就是 agent 上下文</td><td className="no">为人类读者而设计</td></tr>
            <tr><td>本地优先、离线、文件在你硬盘上</td><td className="no">托管 substrate</td><td className="yes">你拥有的本地 markdown</td></tr>
            <tr><td>插件生态 &amp; 个人 PKM</td><td className="partial">不是目标</td><td className="yes">它的主场</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Obsidian <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">本地优先 &amp; 隐私</h3>
            <p>你的库就是自己硬盘上的纯 markdown 文件。没有服务器，能离线用，除非你选择，否则什么都不出本机。对一个想留十年的个人知识库，这份「我拥有」就是对的默认——而 Draftlize 作为托管 substrate，在这点上比不了。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">插件生态 &amp; 灵活</h3>
            <p>日记、canvas、Dataview、几百个社区插件——Obsidian 能弯成几乎任何个人工作流。Draftlize 是刻意的窄：给产品决策一种有主见的形态，而不是一张任你捏的空白画布。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">给未来的你读的思考</h3>
            <p>读书笔记、资料、日志、卡片盒——任何读者是「一个人回头读自己想法」的场景，Obsidian 都很出色。Draftlize 是给首要读者是 AI agent（而非未来的人）的场景，而这会彻底改变格式的样子。</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Draftlize <em>赢的地方。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">改一个决策，下游自动跟着变</h3>
            <p>推翻上游一个定价决策，所有靠它支撑的 spec、PRD 和下游决策都会被自动标成 stale。Obsidian 的双链能告诉你谁链到了谁，但笔记永远不会变 stale——受影响的地方得你一个个手动去翻。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">读者是 agent，而且它能写回</h3>
            <p>当 Claude Code 或 Cursor 在干活时，它既要读你的决策，也要结构化地记录新决策。经 MCP，agent 读卡片图、创建或更新卡片。在 Obsidian 里 agent 也能改文本，但那仍是松散的散文——没有决策身份、没有依赖、没有可追踪的东西。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">是决策，不是笔记</h3>
            <p>一个决策有归属、有状态、有它依赖的东西、也有依赖它的东西。一条 markdown 笔记什么都没有——它只是文本。Draftlize 把决策做成一等单元，于是一个项目的推理变成一张你（和你的 agent）能查询的图，而不是一个你来回滚动的文件夹。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Obsidian 是你存「读过什么」的地方。Draftlize 是你的决策为从它往下做的 agent 活着的地方。
        </blockquote>
        <cite>不是替代品——一个是你的知识库，一个是你的决策 substrate。</cite>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">用 $5 <em>免费开始。</em></h2>
        </div>
        <div className="trust-strip">
          <span>注册送 $5</span>
          <span>按量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            你读的笔记继续用 Obsidian。加 Draftlize 给那些需要 AI agent 来读、写、并保持同步的产品决策。
          </p>
          <a className="btn-primary" href="/">用 $5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
